import {
  Card,
  CircularProgress,
  Grid2,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UploadIcon } from "components/Icons";
import dayjs from "dayjs";
import { useRef } from "react";
import toast from "react-hot-toast";
import axiosInstance from "utils/axiosInstance";
import { moneyCommaSeparator } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import CompanyLogo from "../CompanyLogo";

interface PortfolioCardProps {
  companyDetails?: any;
}

function CompanyCard(props: PortfolioCardProps) {
  const { companyDetails } = props;
  const gcode = companyDetails?.data?.gcode;

  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: uploadLogo, isLoading: isUploading } = useMutation({
    mutationFn: (file: File) => {
      const logoData = new FormData();
      logoData.append("logo", file);
      return axiosInstance.post(`/admin-instrument/${gcode}/logo/`, logoData);
    },
    onSuccess: (response) => {
      toast.success(response?.data?.message || "Logo uploaded");
      // Refetches this company's details (new logo) and the Company Master table
      queryClient.invalidateQueries({ queryKey: [QueryKeys.COMPANY_LIST] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.COMPANY_NAMES] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const fields = [
    {
      name: "Company Name",
      value: companyDetails?.data?.name || "-",
      grid: 5,
    },
    {
      name: "ISIN",
      value: companyDetails?.data?.isinCode || "-",
      grid: 2.33,
    },
    {
      name: "NSE Code",
      value: companyDetails?.data?.nseCode || "-",
      grid: 2.33,
    },
    {
      name: "BSE Code",
      value: companyDetails?.data?.bseCode || "-",
      grid: 2.33,
    },
  ];

  return (
    <Card style={{ margin: 0 }}>
      <Grid2 container spacing={2}>
        <Grid2
          container
          size={9}
          mb={2}
          className="text-[#006480] text-[25px] mt-[-6px] font-bold relative"
        >
          <Grid2 display="flex" alignItems="center" columnGap={1}>
            <CompanyLogo logo={companyDetails?.data?.logo} />
            <Typography fontSize={25} fontWeight={700}>
              {companyDetails?.data?.name}:
            </Typography>

            {companyDetails?.data?.instType === "OPTIONS" && (
              <Typography fontSize={20} fontWeight={600}>
                {dayjs(companyDetails?.data?.expiryDate).format("DD-MM-YYYY")}
              </Typography>
            )}
          </Grid2>

          <Grid2 ml={1}>
            <Typography
              fontSize={25}
              fontWeight={700}
              sx={{ color: "var(--primary-700)" }}
            >
              {moneyCommaSeparator(Number(companyDetails?.data?.cmp))}
            </Typography>
          </Grid2>
        </Grid2>
        <Grid2
          size={3}
          display={"flex"}
          justifyContent={"flex-end"}
          alignItems={"center"}
        >
          <Typography fontSize={14} fontWeight={500}>
            {companyDetails?.data?.timestamp}
          </Typography>
          <Tooltip title="Upload Logo" arrow>
            <span>
              <IconButton
                size="small"
                sx={{ ml: 1 }}
                disabled={isUploading || !gcode}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <UploadIcon width={18} height={18} />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <input
            hidden
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) uploadLogo(file);
              // Reset so the same file can be picked again after a failure
              event.target.value = "";
            }}
          />
        </Grid2>
        <Grid2 container size={12} spacing={1}>
          {fields?.map((dataItem, index: number) => (
            <Grid2 size={dataItem.grid} key={index}>
              <Card
                sx={{
                  margin: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  backgroundColor: "var(--primary-50)",
                  color: "var(--primary-900)",
                  borderRadius: 2,
                  boxShadow: 0.5,
                }}
              >
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  sx={{ color: "var(--primary-700)" }}
                >
                  {dataItem.name}
                </Typography>
                <Typography fontSize={14} fontWeight={700}>
                  {dataItem.value}
                </Typography>
              </Card>
            </Grid2>
          ))}
        </Grid2>
      </Grid2>
    </Card>
  );
}

export default CompanyCard;
